// PlanCheckoutModal.jsx

import React, { useEffect, useMemo, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Utilities
import {
    getPlanCheckoutChargeSummary,
} from '../utils/settingsPlanPaneUtils';

// Services
import { waitForActiveBaseBilling } from 'services/billing/BaseBillingService';
import {
    createBaseSetupIntent,
    createBaseStripeSubscription,
    refreshBaseBillingFromStripe,
} from 'services/billing/BaseSubscriptionWriteService';

// Hooks
import useBaseSeatLimit from 'hooks/useBaseSeatLimit';

// CSS
import './PlanCheckoutModal.css';

// Components
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import Button from 'components/button/Button';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalButtonSection from 'components/modal/buttonsection/ModalButtonSection';
import ModalField from 'components/modal/field/ModalField';
import ModalForm from 'components/modal/form/ModalForm';
import ModalHint from 'components/modal/hint/ModalHint';
import ModalLabel from 'components/modal/label/ModalLabel';
import Modal from 'components/modal/Modal';
import TextInput from 'components/textinput/TextInput';

const CARD_ELEMENT_OPTIONS = {
    style: {
        base: {
            color: '#FFFFFF',
            fontSize: '15px',
            '::placeholder': {
                color: '#8A8A8A',
            },
        },
        invalid: {
            color: '#FF8F8F',
        },
    },
};

function getInitialBillingEmail({ billingRecord, user }) {
    return String(
        billingRecord?.billingEmail
        || user?.data_profile?.email
        || user?.email
        || ''
    ).trim();
}

function getInitialBillingName({ billingRecord, user }) {
    return String(
        billingRecord?.billingName
        || user?.data_profile?.name
        || user?.title
        || ''
    ).trim();
}

/**
 * PlanCheckoutModal
 *
 * Opens paid-plan checkout in a modal layered above space settings.
 *
 * @param {boolean} isOpen - Whether the checkout modal is open.
 * @param {function} onClose - Called when checkout is dismissed.
 * @param {object} billingRecord - Current base billing record.
 * @param {object} plan - Selected paid plan.
 * @param {object} user - Active user model.
 * @param {object} base - Active base model.
 * @param {function} onSuccess - Called after successful subscription creation.
 *
 * @returns {ReactNode} The rendered checkout modal.
 */
const PlanCheckoutModal = React.memo(({
    isOpen,
    onClose,
    billingRecord,
    plan,
    user,
    base,
    onSuccess,
}) => {

    // ----- CONTEXT ----- //

    const { theme } = useTheme();

    // ----- DEVICE DETECTION ----- //

    const isMobile = useIsMobile();
    // ----- HOOKS ----- //

    const stripe = useStripe();
    const elements = useElements();
    const {
        loading: seatSummaryLoading,
        memberCount,
        pendingInviteCount,
    } = useBaseSeatLimit({
        canListPendingInvites: isOpen,
        isActive: isOpen,
        memberCount: 0,
        planKey: plan?.key,
        baseKey: base?.key,
        baseMemberCount: base?.memberCount,
    });

    // ----- STATE ----- //

    const [billingEmail, setBillingEmail] = useState(() => getInitialBillingEmail({ billingRecord, user }));
    const [billingName, setBillingName] = useState(() => getInitialBillingName({ billingRecord, user }));
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    // ----- MEMOS ----- //

    const initialBillingEmail = useMemo(() => {
        return getInitialBillingEmail({ billingRecord, user });
    }, [billingRecord, user]);

    const initialBillingName = useMemo(() => {
        return getInitialBillingName({ billingRecord, user });
    }, [billingRecord, user]);

    const canSubmit = useMemo(() => {
        return Boolean(
            stripe
            && elements
            && !isSubmitting
            && String(billingEmail || '').trim()
            && String(billingName || '').trim()
            && String(plan?.stripePriceId || '').trim()
            && String(plan?.key || '').trim()
            && String(base?.key || '').trim()
        );
    }, [billingEmail, billingName, elements, isSubmitting, plan?.key, plan?.stripePriceId, stripe, base?.key]);

    const modalTitle = plan?.title
        ? `Subscribe to ${plan.title}`
        : 'Subscribe';

    const checkoutChargeSummary = useMemo(() => {
        return getPlanCheckoutChargeSummary({
            memberCount,
            pendingInviteCount,
            plan,
        });
    }, [memberCount, pendingInviteCount, plan]);

    // ----- EFFECTS ----- //

    useEffect(() => {
        setBillingEmail(initialBillingEmail);
    }, [initialBillingEmail]);

    useEffect(() => {
        setBillingName(initialBillingName);
    }, [initialBillingName]);

    useEffect(() => {
        if (!isOpen) {
            setErrorMessage('');
            setIsSubmitting(false);
        }
    }, [isOpen]);

    // ----- HANDLERS ----- //

    /**
     * Creates a setup intent, confirms the card, and creates the subscription.
     */

    const handleSubscribe = async () => {
        if (!canSubmit) return;

        const cardElement = elements.getElement(CardElement);
        if (!cardElement) {
            setErrorMessage('Payment form is not ready yet.');
            return;
        }

        setErrorMessage('');
        setIsSubmitting(true);

        try {
            const setupIntentData = await createBaseSetupIntent({
                baseKey: base.key,
                email: String(billingEmail || '').trim(),
                name: String(billingName || '').trim(),
            });

            const clientSecret = String(setupIntentData?.clientSecret || '').trim();
            if (!clientSecret) {
                throw new Error('Stripe setup intent did not return a client secret.');
            }

            const setupResult = await stripe.confirmCardSetup(clientSecret, {
                payment_method: {
                    billing_details: {
                        email: String(billingEmail || '').trim(),
                        name: String(billingName || '').trim(),
                    },
                    card: cardElement,
                },
            });

            if (setupResult.error) {
                throw new Error(setupResult.error.message || 'Unable to confirm payment method.');
            }

            const paymentMethod = setupResult?.setupIntent?.payment_method;
            const paymentMethodId = typeof paymentMethod === 'string'
                ? paymentMethod
                : String(paymentMethod?.id || '').trim();

            if (!paymentMethodId) {
                throw new Error('Stripe setup intent did not return a payment method.');
            }

            const subscriptionResult = await createBaseStripeSubscription({
                baseKey: base.key,
                email: String(billingEmail || '').trim(),
                name: String(billingName || '').trim(),
                paymentMethodId,
                planId: String(plan.stripePriceId || '').trim(),
                planKey: String(plan.key || '').trim(),
                userKey: user?.key || '',
            });

            const subscriptionStatus = String(subscriptionResult?.status || '').trim().toLowerCase();

            if (subscriptionResult?.requiresPaymentConfirmation) {
                const paymentIntentClientSecret = String(subscriptionResult?.paymentIntentClientSecret || '').trim();
                if (!paymentIntentClientSecret) {
                    throw new Error('Payment confirmation is required, but Stripe did not return a client secret.');
                }

                const paymentResult = await stripe.confirmCardPayment(paymentIntentClientSecret, {
                    payment_method: paymentMethodId,
                });

                if (paymentResult.error) {
                    throw new Error(paymentResult.error.message || 'Unable to confirm subscription payment.');
                }
            } else if (subscriptionStatus === 'incomplete') {
                throw new Error('Unable to start subscription billing. The first invoice payment could not be initialized.');
            }

            if (subscriptionStatus === 'active' || subscriptionStatus === 'trialing') {
                await refreshBaseBillingFromStripe({ baseKey: base.key });
            } else {
                await waitForActiveBaseBilling(base.key, {
                    syncFn: () => refreshBaseBillingFromStripe({ baseKey: base.key }),
                    timeoutMs: 20000,
                });
            }

            // Close before parent plan assignment so checkout doesn't linger on "Subscribing...".
            onClose && onClose();

            if (onSuccess) {
                await onSuccess({
                    billingEmail: String(billingEmail || '').trim(),
                    billingName: String(billingName || '').trim(),
                    subscriptionResult,
                });
            }
        } catch (error) {
            setErrorMessage(error?.message || 'Failed to subscribe to this space plan.');
        } finally {
            setIsSubmitting(false);
        }
    };
    if (!plan) return null;

    // ----- RENDER ----- //

    return (
        <Modal
          iconKey="paid"
          title={modalTitle}
          isOpen={isOpen}
          height="auto"
          width={isMobile ? '100%' : 520}
          onClose={onClose}
        >
            <ModalForm>
                {checkoutChargeSummary && (
                    <ModalField>

                        {/* PLAN CHECKOUT CHARGE CARD */}
                        <div
                          className="settings-modal-plan-summary-card settings-modal-plan-checkout-charge-card"
                        >
                            <div className="settings-modal-plan-summary-header">
                                <div
                                  className="settings-modal-plan-summary-title"
                                  style={{
                                        color: theme.foreground,
                                    }}
                                >
                                    Monthly charge
                                </div>
                                <div
                                  className="settings-modal-plan-summary-price"
                                  style={{
                                        color: theme.foreground,
                                    }}
                                >
                                    {seatSummaryLoading
                                        ? 'Calculating...'
                                        : `${checkoutChargeSummary.monthlyTotalText} / month`}
                                </div>
                            </div>

                            {/* PLAN CHECKOUT SUBTITLE */}
                            {!seatSummaryLoading && (
                                <div
                                  className="settings-modal-plan-checkout-subtitle"
                                >
                                    {`${checkoutChargeSummary.seatPriceText} × ${checkoutChargeSummary.seatLabel}`}
                                </div>
                            )}

                            {/* FACTS 
                            <div className="settings-modal-plan-facts">
                                <div className="settings-modal-plan-fact">
                                    <span
                                        className="settings-modal-plan-fact-label"
                                        style={{
                                            color: theme.foregroundMuted,
                                        }}
                                    >
                                        Plan
                                    </span>
                                    <span
                                        className="settings-modal-plan-fact-value"
                                        style={{
                                            color: theme.foreground,
                                        }}
                                    >
                                        {plan.title}
                                    </span>
                                </div>

                                <div className="settings-modal-plan-fact">
                                    <span
                                        className="settings-modal-plan-fact-label"
                                        style={{
                                            color: theme.foregroundMuted,
                                        }}
                                    >
                                        Seats billed
                                    </span>
                                    <span
                                        className="settings-modal-plan-fact-value"
                                        style={{
                                            color: theme.foreground,
                                        }}
                                    >
                                        {seatSummaryLoading
                                            ? '—'
                                            : checkoutChargeSummary.seatLabel}
                                    </span>
                                </div>

                                <div className="settings-modal-plan-fact">
                                    <span
                                        className="settings-modal-plan-fact-label"
                                        style={{
                                            color: theme.foregroundMuted,
                                        }}
                                    >
                                        Based on
                                    </span>
                                    <span
                                        className="settings-modal-plan-fact-value"
                                        style={{
                                            color: theme.foreground,
                                        }}
                                    >
                                        {seatSummaryLoading
                                            ? 'Loading space seats...'
                                            : checkoutChargeSummary.seatsDetailText}
                                    </span>
                                </div>

                                {!seatSummaryLoading && checkoutEffectivePools.map((pool) => (
                                    <div className="settings-modal-plan-fact" key={pool.label}>
                                        <span
                                            className="settings-modal-plan-fact-label"
                                            style={{
                                                color: theme.foregroundMuted,
                                            }}
                                        >
                                            {pool.label}
                                        </span>
                                        <span
                                            className="settings-modal-plan-fact-value"
                                            style={{
                                                color: theme.foreground,
                                            }}
                                        >
                                            {pool.value}
                                            <span
                                                style={{
                                                    color: theme.foregroundMuted,
                                                    display: 'block',
                                                    fontSize: '12px',
                                                    marginTop: '2px',
                                                }}
                                            >
                                                {pool.detail}
                                            </span>
                                        </span>
                                    </div>
                                ))}
                            </div>
                            */}
                        </div>
                    </ModalField>
                )}

                <ModalField>
                    <ModalHint hint="Adding members or pending invites will increase your monthly bill. Removing them will lower it on the next billing cycle." />
                </ModalField>

                <ModalField>
                    <ModalLabel label="Billing contact email" />
                    <TextInput
                      placeholder="name@company.com"
                      value={billingEmail}
                      width="100%"
                      onChange={(event) => setBillingEmail(event.target.value)}
                    />
                </ModalField>

                <ModalField>
                    <ModalLabel label="Billing contact name" />
                    <TextInput
                      placeholder="Billing contact"
                      value={billingName}
                      width="100%"
                      onChange={(event) => setBillingName(event.target.value)}
                    />
                </ModalField>

                <ModalField>
                    <ModalLabel label="Payment card" />

                    {/* PLAN CARD ELEMENT */}
                    <div
                      className="settings-modal-plan-card-element"
                    >
                        <CardElement options={CARD_ELEMENT_OPTIONS} />
                    </div>
                </ModalField>

                {errorMessage && (
                    <div className="settings-modal-error">{errorMessage}</div>
                )}
            </ModalForm>

            <ModalButtons
                style={{
                    justifyContent: 'space-between',
                }}
            >
                <ModalButtonSection align="START">
                    <div />
                </ModalButtonSection>

                <ModalButtonSection align="END">
                    <Button
                      enabled={!isSubmitting}
                      iconKey="close"
                      label="Cancel"
                      onClick={onClose}
                    />
                    <Button
                      enabled={canSubmit}
                      iconKey="check"
                      label={isSubmitting ? 'Subscribing...' : 'Confirm Subscription'}
                      onClick={handleSubscribe}
                    />
                </ModalButtonSection>
            </ModalButtons>
        </Modal>
    );
});

// Dev Tools
PlanCheckoutModal.displayName = 'PlanCheckoutModal';

export default PlanCheckoutModal;
